import { isDefined, memoize } from "@ls/js-utils";
import { format } from "mathjs";
import moment from "moment";
import { createSelector } from "reselect";
import {
  selectEntities,
  selectGetGroupingLevelOption,
} from "state/data/selectors";
import { selectScenarioOptions } from "state/scenario/selector";

export const selectSelectedAsOfDate = (selectionState) => (state) =>
  state.selection[selectionState].asOfDate;
export const selectSymbolicDate = (selectionState) => (state) =>
  state.selection[selectionState].symbolicDate;
export const selectSecurityLabel = (selectionState) => (state) =>
  state.selection[selectionState].securityLabel;
export const selectSelectedAccountEntityId = (selectionState) => (state) =>
  state.selection[selectionState].accountEntityId;
export const selectSelectedBenchmarkEntityId = (selectionState) => (state) =>
  state.selection[selectionState].benchmarkEntityId;
export const selectSelectedGroupingLevels = (selectionState) => (state) =>
  state.selection[selectionState].groupingLevels;
export const selectSelectedGroupingLevelsFilters =
  (selectionState) => (state) =>
    state.selection[selectionState].groupingLevelsFilters;
export const selectSelectedGroupingLevelsFiltersReweight =
  (selectionState) => (state) =>
    state.selection[selectionState].groupingLevelsFiltersReweight;
export const selectSelectedIsLookthru = (selectionState) => (state) =>
  state.selection[selectionState].isLookthru;
export const selectSelectedIsDefaultBenchmark = (selectionState) => (state) =>
  state.selection[selectionState].isDefaultBenchmark;
export const selectSelectedMinBmkWeight = (selectionState) => (state) =>
  state.selection[selectionState].minBenchWeight;
export const selectSelectedMinAccWeight = (selectionState) => (state) =>
  state.selection[selectionState].minAccWeight;
export const selectCovarianceOptions = (state) =>
  state.selection.covarianceOptions;

export const selectSelectedGroupingLevelsEntities = memoize((selectionState) =>
  createSelector(
    selectSelectedGroupingLevels(selectionState),
    selectGetGroupingLevelOption,
    (groupingLevels, getGroupingLevelOption) =>
      groupingLevels?.map((level) => getGroupingLevelOption(level)) ?? []
  )
);

export const selectSelectedAccountEntity = memoize((selectionState) =>
  createSelector(
    selectSelectedAccountEntityId(selectionState),
    selectEntities,
    (entityId, entities) =>
      entities.find((entity) => entity.entityId === entityId)
  )
);

export const selectSelectedBenchmarkEntity = memoize((selectionState) =>
  createSelector(
    selectSelectedBenchmarkEntityId(selectionState),
    selectEntities,
    (entityId, entities) =>
      entities.find((entity) => entity.entityId === entityId)
  )
);

export const selectSelectedAsOfDateEntity = memoize((selectionState) =>
  createSelector(
    selectSelectedAsOfDate(selectionState),
    (selectedAsOfDateValue) =>
      selectedAsOfDateValue ? moment(selectedAsOfDateValue) : null
  )
);

export const selectScenarioValue = (selectionState) => (state) =>
  state.selection[selectionState].scenario;

export const selectBenchmarkScenarioValue = (selectionState) => (state) =>
  state.selection[selectionState].benchmarkScenario;

export const selectScenarioEntity = memoize((selectionState) =>
  createSelector(
    selectScenarioValue(selectionState),
    selectScenarioOptions,
    (value, options) => options?.find((option) => option.value === value)
  )
);

export const selectSideBySideValue = (selectionState) => (state) =>
  state.selection[selectionState].sideBySide;

export const selectIsDisableBenchmarkWorkspace = (selectionState) => (state) =>
  state.selection[selectionState].isDisableBenchmarkWorkspace;

export const selectGetGroupingParams = memoize((selectionState) =>
  createSelector(
    selectSelectedAsOfDate(selectionState),
    selectSelectedAccountEntityId(selectionState),
    selectSelectedBenchmarkEntityId(selectionState),
    selectSelectedGroupingLevels(selectionState),
    selectSelectedGroupingLevelsFilters(selectionState),
    selectSelectedGroupingLevelsFiltersReweight(selectionState),
    selectSelectedIsLookthru(selectionState),
    selectSelectedIsDefaultBenchmark(selectionState),
    selectSelectedMinAccWeight(selectionState),
    selectSelectedMinBmkWeight(selectionState),
    selectScenarioValue(selectionState),
    selectBenchmarkScenarioValue(selectionState),
    selectSideBySideValue(selectionState),
    selectIsDisableBenchmarkWorkspace(selectionState),
    (
      asOfDate,
      accountEntityId,
      benchEntityId,
      groupingLevels,
      groupingLevelsFilters,
      groupingLevelFilterReweight,
      isLookThru,
      isDefaultBenchmark,
      minAccWeight,
      minBmkWeight,
      scenarioValue,
      benchmarkScenarioValue,
      sideBySide,
      isDisableBenchmarkWorkspace
    ) => {
      return (extraParams = {}) => {
        const params = {
          appName: process.env.REACT_APP_ID,
          ids: accountEntityId,
          dates: asOfDate,
          levels: groupingLevels.join(","),
          lookthru: isLookThru,
          group_type: "ENTITY_LIST",
          meta_data: extraParams.meta_data ?? "NONE",
          results: [
            "VALUES_OBJECTS",
            "PRUNE_BUCKETS",
            "COALESCE_NODES",
            "POSITIONS",
            ...(extraParams.results ?? []),
          ],
        };
        if (groupingLevelsFilters?.length) {
          /**
         *
         Examples...
  
         (1) Three INCLUDE filters - the INST_TYPE filter includes more than one value
         (bucket_filter_type is technically optional in this case since all are INCLUDE)
         bucket_filters=OMNI_ASSET_CLASS,Fixed+Income;INST_TYPE,CORPBD,YANKEE;CURRENCY_CODE,USD
         bucket_filter_type=OMNI_ASSET_CLASS%2CINCLUDE%3BINST_TYPE%2CINCLUDE%3BCURRENCY_CODE%2CINCLUDE
  
         (2) One EXCLUDE filter
         bucket_filters=MUNI_LEVEL_1,Revenue
         bucket_filter_type=MUNI_LEVEL_1,EXCLUDE
  
         (3) Both an INCLUDE and an EXCLUDE
         bucket_filters=MSAA_SECURITIZED,SECURITIZED+CREDIT;OUTLOOK,PM,TIK
         bucket_filter_type=MSAA_SECURITIZED,INCLUDE;OUTLOOK,EXCLUDE
         */
          params.bucket_filters = groupingLevelsFilters
            .map(
              (levelFilter) =>
                `${levelFilter.level},${levelFilter?.levelKeys?.join(",")}`
            )
            .join(";"); //level1,levelKey1,levelKey2;
          params.bucket_filter_type = groupingLevelsFilters
            .map(
              (levelFilter) =>
                `${levelFilter.level},${levelFilter.includeExclude}`
            )
            .join(";"); //level1,INCLUDE/EXCLUDE;
          if (groupingLevelFilterReweight) {
            params.enable_root_normalization = true;
          }
        }
        if (extraParams.summary) {
          params.summary = extraParams.summary;
        }
        if (extraParams.security_attributes) {
          params.security_attributes = extraParams.security_attributes;
        }
        if (extraParams.inputs?.length) {
          params.inputs = extraParams.inputs;
        }
        if (extraParams.percentile_analytics?.length) {
          params.percentile_analytics = extraParams.percentile_analytics;
        }
        if (extraParams.percentiles?.length) {
          params.percentiles = extraParams.percentiles;
        }
        if (extraParams.benchesToo) {
          params.benchesToo = extraParams.benchesToo;
        }
        if (extraParams.analytics?.length) {
          params.analytics = extraParams.analytics;
        }
        if (extraParams.beta_factors?.length) {
          params.beta_factors = extraParams.beta_factors;
        }
        if (extraParams.bucket_beta_types?.length) {
          params.bucket_beta_types = extraParams.bucket_beta_types;
        }
        if (extraParams.beta_types?.length) {
          params.beta_types = extraParams.beta_types;
        }
        if (extraParams.te_covars?.length) {
          params.te_covars = extraParams.te_covars;
        }
        if (extraParams.te_factors?.length) {
          params.te_factors = extraParams.te_factors;
        }
        if (!isDefaultBenchmark && benchEntityId) {
          params.alt_bench_ids = benchEntityId;
        }

        if (extraParams?.costBases?.length) {
          params.costBases = extraParams.costBases;
        }
        //ensure blank string is not passed as a value
        if (
          isDefined(minAccWeight) &&
          !(typeof minAccWeight === "string" && minAccWeight.length === 0)
        ) {
          params.acct_prune_threshold = format(minAccWeight / 100, {
            notation: "fixed",
          });
        }
        //ensure blank string is not passed as a value
        if (
          isDefined(minBmkWeight) &&
          !(typeof minBmkWeight === "string" && minBmkWeight.length === 0)
        ) {
          params.bench_prune_threshold = format(minBmkWeight / 100, {
            notation: "fixed",
          });
        }
        if (extraParams.factrib_factors?.length) {
          params.factrib_factors = extraParams.factrib_factors;
        }
        if (extraParams.factrib_periods?.length) {
          params.factrib_periods = extraParams.factrib_periods;
        }
        if (extraParams.trace_pricing_analytics?.length) {
          params.trace_pricing_analytics = extraParams.trace_pricing_analytics;
        }
        if (extraParams.trace_pricing_periods?.length) {
          params.trace_pricing_periods = extraParams.trace_pricing_periods;
        }
        if (extraParams.scenario_factors?.length) {
          params.scenario_factors = extraParams.scenario_factors;
        }
        if (extraParams.scenario_ids?.length) {
          params.scenario_ids = extraParams.scenario_ids;
        }
        if (extraParams.scenario_values?.length) {
          params.scenario_values = extraParams.scenario_values;
        }
        if (extraParams.var_percentiles?.length) {
          params.var_percentiles = extraParams.var_percentiles;
        }
        if (extraParams.var_percentiles?.length) {
          params.var_percentiles = extraParams.var_percentiles;
        }
        if (extraParams.var_types?.length) {
          params.var_types = extraParams.var_types;
        }

        if (scenarioValue && !sideBySide) {
          params.workspace = scenarioValue;
        }

        if (!isDisableBenchmarkWorkspace) {
          params.alt_bench_workspace = !!benchmarkScenarioValue
            ? benchmarkScenarioValue
            : "";
        }

        return params;
      };
    }
  )
);
